import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Landmark, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;
const TYPES = [
  { key: 'paye', label: 'PAYE → URA' },
  { key: 'nssf', label: 'NSSF → NSSF Fund' },
  { key: 'other', label: 'Other Deductions' },
];

export const RemittancesTab = () => {
  const [data, setData] = useState({ items: [], outstanding: {}, total_outstanding: 0 });
  const [dlg, setDlg] = useState(null); // {run_id, period, type, amount}
  const [form, setForm] = useState({ remitted_date: new Date().toISOString().slice(0, 10), reference: '' });

  const load = useCallback(() => {
    axios.get(`${API}/api/payroll/remittances`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await axios.post(`${API}/api/payroll/runs/${dlg.run_id}/remit`, { remit_type: dlg.type, ...form });
      toast.success(`${dlg.type.toUpperCase()} for ${dlg.period} marked as remitted`);
      setDlg(null);
      setForm({ remitted_date: new Date().toISOString().slice(0, 10), reference: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark remitted'); }
  };

  const th = 'px-2 py-2 text-left text-[10px] font-semibold uppercase bg-muted border-b whitespace-nowrap';
  const td = 'px-2 py-2 border-b text-xs tabular-nums';

  const Cell = ({ item, type }) => {
    const due = item.due[type];
    const rem = (item.remittances || {})[type];
    if (due <= 0) return <span className="text-muted-foreground">—</span>;
    if (rem?.remitted) {
      return (
        <div>
          <Badge variant="outline" className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Remitted</Badge>
          <p className="text-[10px] text-muted-foreground mt-0.5">{rem.date}{rem.reference ? ` · ${rem.reference}` : ''}</p>
        </div>
      );
    }
    return (
      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
        onClick={() => setDlg({ run_id: item.run_id, period: item.period, type, amount: due })}
        data-testid={`remit-${type}-${item.period}`}>
        Mark Remitted
      </Button>
    );
  };

  return (
    <div className="space-y-4" data-testid="remittances-tab">
      <div>
        <h3 className="font-heading font-semibold flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Statutory Remittance Tracker</h3>
        <p className="text-xs text-muted-foreground">PAYE and NSSF withheld on paid payrolls that must be remitted to URA / NSSF. Mark each as remitted once paid over.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="remit-kpis">
        {[['Outstanding PAYE', data.outstanding.paye], ['Outstanding NSSF', data.outstanding.nssf], ['Outstanding Other', data.outstanding.other], ['Total Outstanding', data.total_outstanding]].map(([l, v]) => (
          <Card key={l}><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground">{l}</p>
            <p className={`text-sm font-heading font-bold ${(v || 0) > 0 ? 'text-red-600' : 'text-green-700'}`}>{fmt(v)}</p>
          </CardContent></Card>
        ))}
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-auto">
          <table className="w-full min-w-[820px]">
            <thead><tr>
              <th className={th}>Period</th><th className={th}>Paid On</th><th className={th}>Employees</th>
              <th className={th}>PAYE Due</th><th className={th}>PAYE Status</th>
              <th className={th}>NSSF Due (5%+10%)</th><th className={th}>NSSF Status</th>
              <th className={th}>Other Due</th><th className={th}>Other Status</th>
            </tr></thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.run_id} className="hover:bg-muted/40" data-testid={`remit-row-${item.period}`}>
                  <td className={`${td} font-medium`}>{item.period}</td>
                  <td className={td}>{item.payment_date || '—'}</td>
                  <td className={td}>{item.employee_count}</td>
                  <td className={td}>{fmt(item.due.paye)}</td>
                  <td className={td}><Cell item={item} type="paye" /></td>
                  <td className={td}>{fmt(item.due.nssf)}</td>
                  <td className={td}><Cell item={item} type="nssf" /></td>
                  <td className={td}>{item.due.other > 0 ? fmt(item.due.other) : '—'}</td>
                  <td className={td}><Cell item={item} type="other" /></td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground text-sm">No paid payrolls yet — remittances appear after a payroll is marked as paid</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-sm" data-testid="remit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark {dlg && TYPES.find((t) => t.key === dlg.type)?.label} Remitted</DialogTitle>
            <DialogDescription>Period {dlg?.period} · Amount {dlg && fmt(dlg.amount)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Remittance Date</Label>
              <Input type="date" value={form.remitted_date} onChange={(e) => setForm({ ...form, remitted_date: e.target.value })} data-testid="remit-date-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Reference / Receipt No. (optional)</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="remit-reference-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="remit-confirm-btn">Confirm Remittance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemittancesTab;
